import { Check, ChevronRight, CircleCheckBig, Clock, OctagonAlert, RefreshCw } from 'lucide-react';
import type { ComponentType } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTasks } from '../../../lib/tasks';
import { bucketTasks } from '../../../lib/agent-task-buckets';
import { useNewSince } from '../../../lib/use-last-visited';
import { countNewSince } from '../../../lib/summary-freshness';
import { Badge, Card, Typography } from '../ui';
import { AgentEntryRow } from '../agents/AgentEntryRow';
import entryStyles from '../agents/AgentEntryRow.module.css';
import styles from './tasks.module.css';

// The compact tasks summary on Your Agents: just the count per status category and a link into the full
// Tasks list — NOT the list itself. Figma `10261:86167` → the "Space tasks" tile: the check glyph on the
// heading's line (Heading 04, 14px), a row of DS status Badges under it (Waiting / In progress / Done,
// plus Failed only when something failed), and the drill-in chevron beside that whole body, centred
// against the card — like the Knowledge/Files tiles next to it. (Native's `agentTasksSummaryCard` still
// keeps its chevron inline in the heading row — see the tracked divergence in this package's CLAUDE.md.)
// The whole card opens `/tasks`. With no tasks it keeps its place beside the Files tile and states the
// empty case ("Tasks" / "No tasks yet") rather than vanishing.
//
// The heading line also carries a "N new" badge — tasks created since the reader last opened `/tasks`
// on this device (`lib/last-visited.ts` + `lib/summary-freshness.ts`), the same badge the Routines and
// Files tiles beside it wear. It costs nothing extra: the list is already in hand.

// `icon` is the 12px glyph the mobile entry-row badge leads with (Figma 15311:197080), shared 1:1 with
// native's status icons; the desktop summary draws the text-label badge and ignores it.
const CATS = [
  { key: 'waiting' as const, labelKey: 'agents.taskChip.waiting', variant: 'waiting-on-you' as const, icon: Clock },
  { key: 'inProgress' as const, labelKey: 'agents.taskChip.inProgress', variant: 'in-progress' as const, icon: RefreshCw },
  { key: 'done' as const, labelKey: 'agents.taskChip.done', variant: 'done' as const, icon: Check },
  { key: 'failed' as const, labelKey: 'agents.taskChip.failed', variant: 'failed' as const, icon: OctagonAlert },
] satisfies { key: string; labelKey: string; variant: string; icon: ComponentType<{ size?: number }> }[];

// `emptyLabelKey` lets a caller override the no-tasks heading. The narrow Your Agents screen
// passes `agents.tasksOngoing` ("Ongoing Tasks") there; the desktop screen keeps the default "Tasks".
export function TasksSummary({
  emptyLabelKey = 'agents.tasksLabel',
  entryRow = false,
  workspaceId,
  workspaceSlug,
  pageName = 'agents',
}: {
  emptyLabelKey?: string;
  /** Mobile Your Agents: render the compact single-row entry tile with 12px icon-count badges
   *  (Figma 15311:197080), instead of the desktop heading-over-chips summary. */
  entryRow?: boolean;
  /** Scopes the read to one Space (a Space's Dashboard tab). Omit for the account-wide count. */
  workspaceId?: string;
  /** Carried into the link so the Tasks screen opens pre-filtered to that Space. */
  workspaceSlug?: string;
  pageName?: string;
} = {}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const { state } = useTasks(workspaceId);
  // Captured once at mount — see `useNewSince` for why reading the stamp live would make the badge
  // vanish under the reader the moment they came back from `/tasks`.
  const since = useNewSince('tasks');

  if (state.kind !== 'ready') return null;
  const byTab = bucketTasks(state.tasks);
  const counts = { waiting: byTab.waiting.length, inProgress: byTab.inProgress.length, done: byTab.done.length, failed: byTab.failed.length };
  const total = counts.waiting + counts.inProgress + counts.done + counts.failed;
  const empty = total === 0;

  // Show every category that has anything; Failed never shows at zero (it's the exceptional one).
  const shown = CATS.filter((c) => counts[c.key] > 0);
  // `/api/tasks` stamps `''` for a task it has no date for, which is why this goes through
  // `countNewSince`/`createdAtMs` rather than comparing strings: an undated task is never "new".
  const newCount = since === null ? 0 : countNewSince(state.tasks, since);

  const href = workspaceSlug ? `${basePath}/tasks?workspace=${encodeURIComponent(workspaceSlug)}` : `${basePath}/tasks`;
  const openTasks = () => logPageClickBi({ page_name: pageName, button_name: 'open_tasks' });

  // Mobile entry row (Figma 15311:197080): the title, then one 12px icon-count badge per non-empty
  // status on its own status-chip colour, then the drill-in chevron. Mirrors native's `entryRowCard`.
  if (entryRow) {
    return (
      <AgentEntryRow
        title={empty ? t(emptyLabelKey) : t('agents.tasksLabel')}
        href={href}
        onClick={openTasks}
        testId="agentsTasksSummary"
        trailing={
          <span className={entryStyles.badges}>
            {shown.map((c) => (
              <Badge
                key={c.key}
                variant={c.variant}
                label={
                  <span className={entryStyles.countBadge}>
                    <c.icon size={12} aria-hidden />
                    {counts[c.key]}
                  </span>
                }
                aria-label={t(c.labelKey, { count: counts[c.key] })}
              />
            ))}
          </span>
        }
      />
    );
  }

  return (
    <Card
      component="a"
      interactive
      className={styles.summaryCard}
      {...({
        href: workspaceSlug
          ? `${basePath}/tasks?workspace=${encodeURIComponent(workspaceSlug)}`
          : `${basePath}/tasks`,
      } as Record<string, string>)}
      onClick={() => logPageClickBi({ page_name: pageName, button_name: 'open_tasks' })}
    >
      <div className={styles.summaryBody}>
        <span className={styles.summaryTitle}>
          <CircleCheckBig size={16} aria-hidden />
          <Typography variant="h4" color="primary" component="span" className={styles.summaryTitleText}>
            {empty ? t(emptyLabelKey) : t('agents.tasksHeading', { count: total })}
          </Typography>
          {newCount > 0 && <Badge className={styles.summaryNew} label={t('summary.newBadge', { count: newCount })} />}
        </span>
        {empty ? (
          <Typography variant="p2" color="secondary">
            {t('agents.taskChip.none')}
          </Typography>
        ) : (
          <div className={styles.summaryChips}>
            {shown.map((c) => (
              <Badge key={c.key} variant={c.variant} label={t(c.labelKey, { count: counts[c.key] })} />
            ))}
          </div>
        )}
      </div>
      <ChevronRight size={16} className={`${styles.summaryChevron} dir-flip`} aria-hidden />
    </Card>
  );
}
